/**
 * The Report tool's "Feature Report" document — a numbered card per
 * feature (map thumbnail, a headline field, then the rest as icon/label/
 * value rows), under a branded gradient header. Composed onto ONE canvas
 * by hand, the exact same convention `printSheet.ts` already established
 * for the Print tool (hand-drawn canvas, no PDF library anywhere in this
 * app; `printSheet()`/`downloadSheet()` from that file are reused as-is
 * for the actual download/print actions, so both tools produce their
 * document the same way).
 */

import type { Sheet } from './printSheet';
import type { MapCapture } from './mapCapture';

const DPI = 150;
const MM_PER_INCH = 25.4;
const mmToPx = (mm: number) => Math.round((mm / MM_PER_INCH) * DPI);

const PAGE_WIDTH_MM = 210; // A4 portrait width

// BlueView's own brand palette (glass.css's --bv-navy / --bv-primary) —
// ties the report to the app's own identity rather than inventing a new one.
const NAVY = '#0f2942';
const PRIMARY = '#2563eb';
const INK = '#0f172a';
const MUTED = '#64748b';
const LINE = '#e2e8f0';
const PANEL = '#f0f6ff';

export interface ReportBlock {
  thumbnail: MapCapture | null;
  rows: { label: string; value: string }[];
}

/** Trims text to the width available, matching `printSheet.ts`'s own
 *  legend-label truncation so nothing ever overflows its column. */
function fit(context: CanvasRenderingContext2D, text: string, width: number): string {
  if (width <= 0) return '';
  if (context.measureText(text).width <= width) return text;
  let clipped = text;
  while (clipped.length > 1 && context.measureText(`${clipped}…`).width > width) {
    clipped = clipped.slice(0, -1);
  }
  return `${clipped}…`;
}

function roundRectPath(context: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(x + w - radius, y);
  context.quadraticCurveTo(x + w, y, x + w, y + radius);
  context.lineTo(x + w, y + h - radius);
  context.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  context.lineTo(x + radius, y + h);
  context.quadraticCurveTo(x, y + h, x, y + h - radius);
  context.lineTo(x, y + radius);
  context.quadraticCurveTo(x, y, x + radius, y);
  context.closePath();
}

/* ── Small hand-drawn icon set — plain Canvas primitives, not traced from
   an external icon library (nothing to mis-transcribe), matching
   `printSheet.ts`'s own house style for its legend/north-arrow glyphs. ── */

function drawPinGlyph(context: CanvasRenderingContext2D, cx: number, cy: number, size: number, color: string) {
  const r = size * 0.3;
  const bulbY = cy - size * 0.08;
  context.save();
  context.fillStyle = color;
  context.beginPath();
  context.arc(cx, bulbY, r, 0, Math.PI * 2);
  context.fill();
  context.beginPath();
  context.moveTo(cx - r * 0.78, bulbY + r * 0.55);
  context.lineTo(cx + r * 0.78, bulbY + r * 0.55);
  context.lineTo(cx, cy + size * 0.46);
  context.closePath();
  context.fill();
  context.fillStyle = '#ffffff';
  context.beginPath();
  context.arc(cx, bulbY, r * 0.4, 0, Math.PI * 2);
  context.fill();
  context.restore();
}

function drawTagGlyph(context: CanvasRenderingContext2D, cx: number, cy: number, size: number, color: string) {
  context.save();
  context.translate(cx, cy);
  context.rotate(-Math.PI / 4);
  const w = size * 0.62;
  const h = size * 0.44;
  context.fillStyle = color;
  roundRectPath(context, -w / 2, -h / 2, w, h, size * 0.08);
  context.fill();
  context.fillStyle = '#ffffff';
  context.beginPath();
  context.arc(-w / 2 + h / 2, 0, size * 0.07, 0, Math.PI * 2);
  context.fill();
  context.restore();
}

function drawBuildingGlyph(context: CanvasRenderingContext2D, cx: number, cy: number, size: number, color: string) {
  context.save();
  const w = size * 0.56;
  const h = size * 0.66;
  context.fillStyle = color;
  context.fillRect(cx - w / 2, cy - h / 2, w, h);
  context.fillStyle = '#ffffff';
  const cols = 2;
  const rows = 3;
  const padX = w * 0.16;
  const padY = h * 0.12;
  const cellW = (w - padX * 2) / cols - padX * 0.35;
  const cellH = (h - padY * 2) / rows - padY * 0.25;
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const x = cx - w / 2 + padX + col * (cellW + padX * 0.35);
      const y = cy - h / 2 + padY + row * (cellH + padY * 0.25);
      context.fillRect(x, y, cellW, cellH);
    }
  }
  context.restore();
}

function drawDotGlyph(context: CanvasRenderingContext2D, cx: number, cy: number, size: number, color: string) {
  context.save();
  context.fillStyle = color;
  context.beginPath();
  context.arc(cx, cy, size * 0.18, 0, Math.PI * 2);
  context.fill();
  context.restore();
}

/** A small, honest heuristic — the field's NAME decides which generic
 *  glyph it gets (location-ish → pin, category-ish → tag, name-ish →
 *  building), falling back to a plain dot. Not a claim that it always
 *  guesses the "correct" icon, just a consistent, pleasant one. */
function glyphFor(label: string) {
  const lower = label.toLowerCase();
  if (lower.includes('address') || lower.includes('location') || lower === 'xy') return drawPinGlyph;
  if (lower.includes('categ') || lower.includes('type') || lower.includes('class')) return drawTagGlyph;
  if (lower.includes('name') || lower.includes('title')) return drawBuildingGlyph;
  return drawDotGlyph;
}

function drawNorthBadge(context: CanvasRenderingContext2D, x: number, y: number, size: number) {
  context.save();
  context.fillStyle = 'rgba(255,255,255,0.92)';
  context.beginPath();
  context.arc(x, y, size / 2, 0, Math.PI * 2);
  context.fill();
  context.strokeStyle = LINE;
  context.lineWidth = 1;
  context.stroke();
  context.fillStyle = NAVY;
  context.beginPath();
  context.moveTo(x, y - size * 0.3);
  context.lineTo(x + size * 0.14, y + size * 0.12);
  context.lineTo(x, y);
  context.lineTo(x - size * 0.14, y + size * 0.12);
  context.closePath();
  context.fill();
  context.restore();
}

export function renderReportSheet(title: string, subtitle: string, blocks: ReportBlock[]): Sheet {
  const width = mmToPx(PAGE_WIDTH_MM);
  const pageMargin = mmToPx(10);
  const cardX = pageMargin;
  const cardWidth = width - pageMargin * 2;
  const cardPad = mmToPx(4);
  const cardGap = mmToPx(5);
  const thumbW = mmToPx(52);
  const thumbH = mmToPx(34);
  const textGap = mmToPx(4);
  const textX = cardX + cardPad + thumbW + textGap;
  const textWidth = cardX + cardWidth - cardPad - textX;
  const headingBarH = mmToPx(9);
  const rowH = mmToPx(6.2);
  const badgeDia = mmToPx(8);
  const headerH = mmToPx(26);

  const cardHeight = (block: ReportBlock) => {
    // The headline bar's own field is ALSO drawn again as the first row
    // below it (matching the reference's repeated "Name: Indian Bank"
    // row) — every row in `block.rows` gets its own line, not rows.length-1.
    const rightSide = headingBarH + mmToPx(1.5) + block.rows.length * rowH;
    return Math.max(thumbH, rightSide) + cardPad * 2;
  };

  const totalHeight =
    headerH + blocks.reduce((sum, block) => sum + cardHeight(block) + cardGap, 0) + pageMargin;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = Math.max(totalHeight, headerH + pageMargin);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser could not prepare the report.');

  context.fillStyle = '#f8fafc';
  context.fillRect(0, 0, canvas.width, canvas.height);

  /* ── Header band — BlueView's own brand gradient, a pin mark, title + subtitle ── */
  const gradient = context.createLinearGradient(0, 0, width, headerH);
  gradient.addColorStop(0, NAVY);
  gradient.addColorStop(1, PRIMARY);
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, headerH);

  const pinCx = pageMargin + mmToPx(4);
  const pinCy = headerH / 2;
  drawPinGlyph(context, pinCx, pinCy, mmToPx(9), '#ffffff');

  const titleX = pinCx + mmToPx(7);
  context.textAlign = 'left';
  context.fillStyle = '#ffffff';
  context.font = `700 ${Math.round(mmToPx(5.4))}px Inter, system-ui, sans-serif`;
  context.textBaseline = 'alphabetic';
  context.fillText(title || 'Report', titleX, headerH / 2 - mmToPx(1));
  if (subtitle.trim()) {
    context.font = `400 ${Math.round(mmToPx(3))}px Inter, system-ui, sans-serif`;
    context.fillStyle = 'rgba(255,255,255,0.85)';
    context.fillText(subtitle.trim(), titleX, headerH / 2 + mmToPx(5));
  }

  /* ── One card per feature ── */
  let cursorY = headerH + cardGap;

  blocks.forEach((block, index) => {
    const height = cardHeight(block);

    // Card shell.
    context.save();
    context.shadowColor = 'rgba(15, 41, 66, 0.12)';
    context.shadowBlur = mmToPx(2.2);
    context.shadowOffsetY = mmToPx(0.8);
    context.fillStyle = '#ffffff';
    roundRectPath(context, cardX, cursorY, cardWidth, height, mmToPx(3));
    context.fill();
    context.restore();

    context.strokeStyle = LINE;
    context.lineWidth = 1;
    roundRectPath(context, cardX, cursorY, cardWidth, height, mmToPx(3));
    context.stroke();

    // Thumbnail, rounded corners, with a small north badge.
    const thumbX = cardX + cardPad;
    const thumbY = cursorY + cardPad;
    context.save();
    roundRectPath(context, thumbX, thumbY, thumbW, thumbH, mmToPx(2));
    context.clip();
    if (block.thumbnail) {
      context.drawImage(block.thumbnail.canvas, thumbX, thumbY, thumbW, thumbH);
    } else {
      context.fillStyle = '#f1f5f9';
      context.fillRect(thumbX, thumbY, thumbW, thumbH);
      context.fillStyle = MUTED;
      context.font = `400 ${Math.round(mmToPx(2.6))}px Inter, system-ui, sans-serif`;
      context.textAlign = 'center';
      context.fillText('No image', thumbX + thumbW / 2, thumbY + thumbH / 2);
      context.textAlign = 'left';
    }
    context.restore();
    context.strokeStyle = LINE;
    context.lineWidth = 1;
    roundRectPath(context, thumbX, thumbY, thumbW, thumbH, mmToPx(2));
    context.stroke();
    drawNorthBadge(context, thumbX + thumbW - mmToPx(5), thumbY + mmToPx(5), mmToPx(6.5));

    // Number badge, overlapping the card's top-left corner.
    const badgeCx = cardX + mmToPx(1);
    const badgeCy = cursorY + mmToPx(1);
    context.save();
    context.fillStyle = NAVY;
    context.beginPath();
    context.arc(badgeCx, badgeCy, badgeDia / 2, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = '#ffffff';
    context.font = `700 ${Math.round(mmToPx(3))}px Inter, system-ui, sans-serif`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(String(index + 1).padStart(2, '0'), badgeCx, badgeCy + mmToPx(0.3));
    context.restore();
    context.textAlign = 'left';
    context.textBaseline = 'alphabetic';

    // Headline bar — the first row's value, with a small glyph badge.
    const heading = block.rows[0];
    const barY = cursorY + cardPad;
    if (heading) {
      context.fillStyle = PANEL;
      roundRectPath(context, textX, barY, textWidth, headingBarH, mmToPx(1.5));
      context.fill();

      const iconCx = textX + mmToPx(5.5);
      const iconCy = barY + headingBarH / 2;
      context.save();
      context.fillStyle = PRIMARY;
      context.beginPath();
      context.arc(iconCx, iconCy, mmToPx(3.4), 0, Math.PI * 2);
      context.fill();
      context.restore();
      glyphFor(heading.label)(context, iconCx, iconCy, mmToPx(4.4), '#ffffff');

      context.fillStyle = NAVY;
      context.font = `700 ${Math.round(mmToPx(3.4))}px Inter, system-ui, sans-serif`;
      context.textBaseline = 'middle';
      const headingTextX = iconCx + mmToPx(5.5);
      context.fillText(
        fit(context, heading.value || '—', textX + textWidth - mmToPx(2) - headingTextX),
        headingTextX,
        iconCy,
      );
      context.textBaseline = 'alphabetic';
    }

    // Attribute rows, each with a small glyph + "Label : value".
    let rowY = barY + headingBarH + mmToPx(1.5) + rowH / 2;
    for (const row of block.rows) {
      const dotCx = textX + mmToPx(2.2);
      glyphFor(row.label)(context, dotCx, rowY, mmToPx(3.6), PRIMARY);

      const labelX = textX + mmToPx(5.5);
      context.font = `600 ${Math.round(mmToPx(2.8))}px Inter, system-ui, sans-serif`;
      context.fillStyle = INK;
      context.textBaseline = 'middle';
      context.fillText(row.label, labelX, rowY);
      const labelWidth = context.measureText(row.label).width;

      context.font = `400 ${Math.round(mmToPx(2.8))}px Inter, system-ui, sans-serif`;
      context.fillStyle = MUTED;
      const sep = '  :  ';
      context.fillText(sep, labelX + labelWidth, rowY);
      const sepWidth = context.measureText(sep).width;

      context.fillStyle = INK;
      context.fillText(
        fit(context, row.value || '—', Math.max(0, textX + textWidth - (labelX + labelWidth + sepWidth))),
        labelX + labelWidth + sepWidth,
        rowY,
      );
      context.textBaseline = 'alphabetic';

      rowY += rowH;
    }

    cursorY += height + cardGap;
  });

  return { canvas, widthPx: canvas.width, heightPx: canvas.height, cssPageSize: 'A4 portrait' };
}
